import { useEffect, useMemo, useState } from 'react'
import { AdvancedMarker, AdvancedMarkerAnchorPoint, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { Box, Flex, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { LuMapPin as MapPin, LuFlame as Flame, LuWaypoints as Waypoints, LuRoute as Route, LuPause as Pause } from 'react-icons/lu'
import type { ShipmentEventResponse, ShipmentPauseEventResponse } from '../../../../api/models/response/shipment'
import { brand, themeColors } from '../../../../theme'
import { parseUtcDate } from '../../../../lib/dates'
import { getShipmentState } from '../../../../lib/shipmentStates'
import { KMap, Polyline, FitBounds, HeatLayer } from '../../../../lib/googleMaps'
import { ElementPin } from '../../../../lib/mapMarkers'
import {
  haversineMeters,
  formatDuration,
  SPEED_COLORS,
  type EnrichedPoint,
  type ColoredSegment,
  type Stop,
  type PhotoWithTime,
  type SegmentStats,
} from '../../../../lib/trackAnalytics'
import { CARD_SHADOW, ROUTE_COLOR, ROUTE_DIM_COLOR } from './cardStyles'

const MotionBox = motion.create(Box)
const colors = themeColors
const MAX_CLICK_MARKERS = 350

type LL = google.maps.LatLngLiteral
const ll = (lat: number, lng: number): LL => ({ lat, lng })

// ── Marcadores HTML (hijos de AdvancedMarker) ───────────────

function DotMarker({ color, size = 9 }: { color: string; size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        background: color,
        border: '2px solid white',
        borderRadius: '50%',
        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      }}
    />
  )
}

function EndpointMarker({ color, size = 18, ring = false }: { color: string; size?: number; ring?: boolean }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        background: color,
        border: '3px solid white',
        borderRadius: '50%',
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        outline: ring ? `3px solid ${color}66` : undefined,
        outlineOffset: ring ? 1 : undefined,
      }}
    />
  )
}

/** Chip de etiqueta bajo un pin (duración de parada, estado, pausa). */
function MarkerChip({ color, label }: { color: string; label: string }) {
  return (
    <div
      style={{
        marginTop: 2,
        padding: '1px 6px',
        background: 'white',
        color,
        fontSize: 9,
        fontWeight: 700,
        borderRadius: 4,
        border: `1px solid ${color}`,
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      }}
    >
      {label}
    </div>
  )
}

function StopMarker({ order, durationLabel }: { order: number; durationLabel: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ElementPin kind="stop" size={34} badge={order} />
      <MarkerChip color="#c8442f" label={durationLabel} />
    </div>
  )
}

function StateMarker({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ElementPin kind="stateEvent" color={color} size={30} />
      <MarkerChip color={color} label={label} />
    </div>
  )
}

// ── Capa de mapa de calor ───────────────────────────────────

// Google retiró visualization.HeatmapLayer en la v3.65 del API: el constructor
// sigue existiendo pero LANZA al instanciarse, así que el guard de
// `typeof Ctor === 'function'` que había aquí ya no protegía de nada. Se dibuja
// con la HeatLayer propia (círculos ponderados), sin depender de esa librería.
const ROUTE_HEAT_GRADIENT = [
  'rgba(42, 138, 135,0)',
  '#2a8a87',
  '#EAB308',
  '#F97316',
  '#c8442f',
  '#b23a26',
]

/**
 * Peso por punto del recorrido: cuanto más lento fue el camión, más caliente.
 * Una parada larga (más de 10 minutos desde el punto anterior) sube el peso,
 * porque detenerse es justo lo que interesa ver sobre la ruta.
 */
function HeatmapLayer({ enriched, visible }: { enriched: EnrichedPoint[]; visible: boolean }) {
  const points = useMemo(
    () =>
      enriched.map((p, i) => {
        let weight = 0.3
        if (i > 0) {
          const s = p.speedKmh
          if (s < 5) weight = 1.0
          else if (s < 20) weight = 0.7
          else if (s < 40) weight = 0.4
          else weight = 0.2
          if (p.dtFromPrevS / 60 > 10) weight = Math.min(1.0, weight + 0.3)
        }
        return { lat: p.lat, lng: p.lng, weight }
      }),
    [enriched],
  )

  return (
    <HeatLayer
      points={points}
      max={1}
      gradient={ROUTE_HEAT_GRADIENT}
      // Radio en metros: sobre una ruta se lee a escala de decenas de metros.
      radiusMeters={60}
      opacity={0.7}
      visible={visible && enriched.length >= 2}
    />
  )
}

// ── Vuelo a un punto enfocado ───────────────────────────────

function FlyTo({ focus }: { focus: { lat: number; lng: number; nonce: number } | null }) {
  const map = useMap()
  useEffect(() => {
    if (!map || !focus) return
    map.panTo(ll(focus.lat, focus.lng))
    const z = map.getZoom()
    if (z == null || z < 15) map.setZoom(15)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.nonce])
  return null
}

// ── Component ───────────────────────────────────────────────

interface Props {
  shipmentId: string
  enriched: EnrichedPoint[]
  coloredSegments: ColoredSegment[]
  stops: Stop[]
  photos: PhotoWithTime[]
  stateChanges: ShipmentEventResponse[]
  pauseEvents: ShipmentPauseEventResponse[]
  snappedRoutePolyline?: string | null
  showSnapped: boolean
  snapping: boolean
  onSnapRoute: () => void
  showHeatmap: boolean
  colorBySpeed: boolean
  onToggleSnapped: () => void
  onToggleHeatmap: () => void
  onToggleColorBySpeed: () => void
  selection: { a: number | null; b: number | null }
  segment: SegmentStats | null
  onPointClick: (index: number) => void
  focus: { lat: number; lng: number; nonce: number } | null
  onPhotoClick: (photoIndex: number) => void
  children?: React.ReactNode
}

function clock(iso: string): string {
  return parseUtcDate(iso).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function dateTime(iso: string): string {
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  )
}

function PauseMarker({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ElementPin kind="pause" size={30} />
      <MarkerChip color="#8a5f0b" label={label} />
    </div>
  )
}

type InfoState =
  | { kind: 'point'; index: number }
  | { kind: 'stop'; index: number }
  | { kind: 'event'; index: number }
  | { kind: 'photo'; index: number }
  | { kind: 'pause'; index: number }
  | null

export default function RouteMap({
  shipmentId,
  enriched,
  coloredSegments,
  stops,
  photos,
  stateChanges,
  pauseEvents,
  snappedRoutePolyline,
  showSnapped,
  snapping,
  onSnapRoute,
  showHeatmap,
  colorBySpeed,
  onToggleSnapped,
  onToggleHeatmap,
  onToggleColorBySpeed,
  selection,
  segment,
  onPointClick,
  focus,
  onPhotoClick,
  children,
}: Props) {
  void shipmentId
  const n = enriched.length
  const [info, setInfo] = useState<InfoState>(null)

  // Ruta ajustada a la red vial (Snap to Roads). Llega como polilínea codificada de
  // Google; la decodificamos con la librería geometry. Si no hay polilínea o la librería
  // aún no cargó, queda vacía y se muestra la traza cruda.
  const geometryLib = useMapsLibrary('geometry')
  const snappedPath = useMemo<LL[]>(() => {
    if (!geometryLib || !snappedRoutePolyline) return []
    try {
      return geometryLib.encoding.decodePath(snappedRoutePolyline).map((p) => ll(p.lat(), p.lng()))
    } catch {
      return []
    }
  }, [geometryLib, snappedRoutePolyline])
  const hasSnapped = snappedPath.length > 1
  const useSnapped = showSnapped && hasSnapped

  const positions = useMemo<LL[]>(() => enriched.map((p) => ll(p.lat, p.lng)), [enriched])
  const geoPhotos = useMemo(
    () =>
      photos
        .map((p, idx) => ({ p, idx }))
        .filter(({ p }) => p.photo.latitude != null && p.photo.longitude != null),
    [photos],
  )
  const allPositions = useMemo<LL[]>(
    () => [
      ...positions,
      ...geoPhotos.map(({ p }) => ll(p.photo.latitude as number, p.photo.longitude as number)),
      ...stateChanges
        .filter((sc) => sc.latitude != null && sc.longitude != null)
        .map((sc) => ll(sc.latitude as number, sc.longitude as number)),
    ],
    [positions, geoPhotos, stateChanges],
  )

  // Marcadores de pausa: cada evento "pausar" con coords, con su motivo y la duración hasta el
  // "reanudar" siguiente. Explican los huecos de la traza.
  const pausePins = useMemo(() => {
    const pins: { lat: number; lng: number; reason: string; occurredAt: string; durationLabel: string }[] = []
    for (let i = 0; i < pauseEvents.length; i++) {
      const e = pauseEvents[i]
      if (e.actionType !== 'pausar' || e.latitude == null || e.longitude == null) continue
      const reason = e.reasonText || e.reasonName || 'Pausa'
      let durationLabel = 'en pausa'
      const resume = pauseEvents.slice(i + 1).find((r) => r.actionType === 'reanudar')
      if (resume) {
        const ms = parseUtcDate(resume.occurredAt).getTime() - parseUtcDate(e.occurredAt).getTime()
        if (ms > 0) durationLabel = formatDuration(ms)
      }
      pins.push({ lat: e.latitude, lng: e.longitude, reason, occurredAt: e.occurredAt, durationLabel })
    }
    return pins
  }, [pauseEvents])
  const boundsNonce = allPositions.length
  const dimmed = segment != null

  // Marcadores intermedios decimados para no saturar el render
  const interactiveIndices = useMemo(() => {
    const step = Math.max(1, Math.ceil((n - 2) / MAX_CLICK_MARKERS))
    const set = new Set<number>()
    for (let i = 1; i < n - 1; i += step) set.add(i)
    if (selection.a != null) set.add(selection.a)
    if (selection.b != null) set.add(selection.b)
    set.delete(0)
    set.delete(n - 1)
    return [...set]
  }, [n, selection.a, selection.b])

  const segPositions = segment != null ? positions.slice(segment.fromIndex, segment.toIndex + 1) : []

  const handleLineClick = (e: google.maps.PolyMouseEvent) => {
    if (!e.latLng) return
    const lat = e.latLng.lat()
    const lng = e.latLng.lng()
    let best = 0
    let bestD = Infinity
    for (const p of enriched) {
      const d = haversineMeters(lat, lng, p.lat, p.lng)
      if (d < bestD) {
        bestD = d
        best = p.index
      }
    }
    onPointClick(best)
  }

  return (
    <MotionBox
      bg={colors.bgPanel}
      border="1px solid"
      borderColor={colors.border}
      borderRadius="2xl"
      overflow="hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      boxShadow={CARD_SHADOW}
    >
      {/* Header */}
      <Flex
        p={5}
        borderBottom="1px solid"
        borderColor={colors.border}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Flex alignItems="center" gap={2}>
          <MapPin size={16} color={brand.green} />
          <Text fontSize="md" fontWeight="bold" color={colors.text}>
            Recorrido GPS
          </Text>
          <Text fontSize="xs" color={colors.textMuted} ml={2}>
            {n} puntos
            {geoPhotos.length > 0 && ` · ${geoPhotos.length} foto${geoPhotos.length > 1 ? 's' : ''}`}
            {stops.length > 0 && ` · ${stops.length} parada${stops.length > 1 ? 's' : ''}`}
          </Text>
        </Flex>
        <Flex gap={2}>
          {hasSnapped ? (
            <ToggleButton active={showSnapped} onClick={onToggleSnapped} activeColor="#1A7A4C" icon={<Route size={14} />}>
              Ruta ajustada
            </ToggleButton>
          ) : (
            n > 1 && (
              <Flex
                as="button"
                alignItems="center"
                gap={2}
                px={3}
                py={1.5}
                borderRadius="lg"
                cursor={snapping ? 'wait' : 'pointer'}
                fontSize="xs"
                fontWeight="600"
                bg="#1A7A4C20"
                color="#1A7A4C"
                border="1px solid"
                borderColor="#1A7A4C40"
                opacity={snapping ? 0.7 : 1}
                _hover={{ bg: '#1A7A4C30' }}
                transition="all 0.2s"
                onClick={snapping ? undefined : onSnapRoute}
                title="Pega la traza GPS a la red vial (Google Roads)"
              >
                <Route size={14} />
                {snapping ? 'Afinando…' : 'Afinar ruta'}
              </Flex>
            )
          )}
          <ToggleButton active={colorBySpeed} onClick={onToggleColorBySpeed} activeColor="#1A7A4C" icon={<Waypoints size={14} />}>
            Color por velocidad
          </ToggleButton>
          <ToggleButton active={showHeatmap} onClick={onToggleHeatmap} activeColor="#c8442f" icon={<Flame size={14} />}>
            Mapa de calor
          </ToggleButton>
        </Flex>
      </Flex>

      {/* Hint */}
      <Box px={5} py={2} bg={colors.bgPanel} borderBottom="1px solid" borderColor={colors.border}>
        <Text fontSize="11px" color={colors.textMuted}>
          Haz clic en dos puntos (o sobre la ruta) para medir el tramo entre ellos.
        </Text>
      </Box>

      <Box position="relative" height={{ base: '360px', md: '500px' }}>
        <KMap defaultCenter={ll(-14.0, -74.0)} defaultZoom={6} mapTypeId="hybrid" disableDefaultUI>
          <FitBounds points={allPositions} padding={50} maxZoom={15} nonce={boundsNonce} />
          <FlyTo focus={focus} />
          <HeatmapLayer enriched={enriched} visible={showHeatmap} />

          {/* Ruta base — snapeada a la red vial por defecto; cruda si el usuario lo
              alterna; atenuada a gris cuando hay un tramo seleccionado */}
          {!showHeatmap &&
            (dimmed ? (
              <Polyline path={positions} strokeColor={ROUTE_DIM_COLOR} strokeWeight={3} strokeOpacity={0.55} />
            ) : useSnapped ? (
              <Polyline path={snappedPath} strokeColor={ROUTE_COLOR} strokeWeight={4} strokeOpacity={0.95} />
            ) : colorBySpeed ? (
              coloredSegments.map((s, i) => (
                <Polyline
                  key={`seg-${i}`}
                  path={s.positions.map((p) => ll(p[0], p[1]))}
                  strokeColor={s.color}
                  strokeWeight={4}
                  strokeOpacity={0.95}
                />
              ))
            ) : (
              <Polyline path={positions} strokeColor={ROUTE_COLOR} strokeWeight={4} strokeOpacity={0.92} />
            ))}

          {/* Tramo A→B resaltado */}
          {segPositions.length > 1 && (
            <Polyline path={segPositions} strokeColor={brand.gold} strokeWeight={6} strokeOpacity={0.95} zIndex={10} />
          )}

          {/* Capa transparente para click sobre la ruta */}
          {positions.length > 1 && (
            <Polyline path={positions} strokeColor="#000" strokeWeight={18} strokeOpacity={0} onClick={handleLineClick} />
          )}

          {/* Puntos intermedios (clic para seleccionar) */}
          {interactiveIndices.map((i) => (
            <AdvancedMarker
              key={`pt-${i}`}
              position={positions[i]}
              anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
              zIndex={2}
              onClick={() => onPointClick(i)}
              onMouseEnter={() => setInfo({ kind: 'point', index: i })}
              onMouseLeave={() => setInfo((cur) => (cur?.kind === 'point' && cur.index === i ? null : cur))}
            >
              <DotMarker color={brand.green} size={9} />
            </AdvancedMarker>
          ))}

          {/* Origen / Destino */}
          {n > 0 && (
            <AdvancedMarker
              position={positions[0]}
              zIndex={3}
              onClick={() => onPointClick(0)}
              onMouseEnter={() => setInfo({ kind: 'point', index: 0 })}
              onMouseLeave={() => setInfo((cur) => (cur?.kind === 'point' && cur.index === 0 ? null : cur))}
            >
              <ElementPin kind="origin" size={34} />
            </AdvancedMarker>
          )}
          {n > 1 && (
            <AdvancedMarker
              position={positions[n - 1]}
              zIndex={3}
              onClick={() => onPointClick(n - 1)}
              onMouseEnter={() => setInfo({ kind: 'point', index: n - 1 })}
              onMouseLeave={() => setInfo((cur) => (cur?.kind === 'point' && cur.index === n - 1 ? null : cur))}
            >
              <ElementPin kind="destination" size={34} />
            </AdvancedMarker>
          )}

          {/* Marcadores A / B */}
          {selection.a != null && selection.a !== 0 && selection.a !== n - 1 && (
            <AdvancedMarker position={positions[selection.a]} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} zIndex={4}>
              <EndpointMarker color={brand.greenLight} size={20} ring />
            </AdvancedMarker>
          )}
          {selection.b != null && selection.b !== 0 && selection.b !== n - 1 && (
            <AdvancedMarker position={positions[selection.b]} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} zIndex={4}>
              <EndpointMarker color={brand.gold} size={20} ring />
            </AdvancedMarker>
          )}

          {/* Paradas */}
          {stops.map((stop, idx) => (
            <AdvancedMarker
              key={`stop-${stop.startIndex}`}
              position={ll(stop.lat, stop.lng)}
              zIndex={5}
              onClick={() => setInfo({ kind: 'stop', index: idx })}
            >
              <StopMarker order={idx + 1} durationLabel={formatDuration(stop.durationMs)} />
            </AdvancedMarker>
          ))}

          {/* Eventos de estado */}
          {stateChanges.map((sc, idx) => {
            if (sc.latitude == null || sc.longitude == null) return null
            const def = getShipmentState(sc.shipmentState)
            return (
              <AdvancedMarker
                key={`event-${sc.id}`}
                position={ll(sc.latitude, sc.longitude)}
                zIndex={6}
                onClick={() => setInfo({ kind: 'event', index: idx })}
              >
                <StateMarker color={def.color} label={`${idx + 1}. ${def.label}`} />
              </AdvancedMarker>
            )
          })}

          {/* Pausas del conductor (motivo + duración) — explican los huecos de la traza */}
          {pausePins.map((p, idx) => (
            <AdvancedMarker
              key={`pause-${idx}`}
              position={ll(p.lat, p.lng)}
              zIndex={6}
              onClick={() => setInfo({ kind: 'pause', index: idx })}
            >
              <PauseMarker label={`Pausa · ${p.durationLabel}`} />
            </AdvancedMarker>
          ))}

          {/* Fotos */}
          {geoPhotos.map(({ p, idx }) => (
            <AdvancedMarker
              key={`photo-${p.photo.id}`}
              position={ll(p.photo.latitude as number, p.photo.longitude as number)}
              zIndex={5}
              onClick={() => onPhotoClick(idx)}
              onMouseEnter={() => setInfo({ kind: 'photo', index: idx })}
              onMouseLeave={() => setInfo((cur) => (cur?.kind === 'photo' && cur.index === idx ? null : cur))}
            >
              <ElementPin kind="photo" size={32} />
            </AdvancedMarker>
          ))}

          {/* InfoWindow contextual */}
          {info?.kind === 'point' && enriched[info.index] && (
            <InfoWindow position={positions[info.index]} pixelOffset={[0, -10]} disableAutoPan headerDisabled>
              <Box fontFamily="'Instrument Sans', sans-serif" p={1}>
                <Text fontSize="xs" fontWeight="bold" color="#1a1a1a">
                  {info.index === 0 ? 'Origen' : info.index === n - 1 ? 'Destino' : `Punto ${info.index + 1}`}
                </Text>
                <Text fontSize="11px" color="#555">
                  {clock(enriched[info.index].capturedAt)}
                </Text>
                <Text fontSize="10px" color="#888">
                  {enriched[info.index].speedKmh.toFixed(1)} km/h
                </Text>
              </Box>
            </InfoWindow>
          )}
          {info?.kind === 'photo' && geoPhotos[info.index] && (
            <InfoWindow
              position={ll(geoPhotos[info.index].p.photo.latitude as number, geoPhotos[info.index].p.photo.longitude as number)}
              pixelOffset={[0, -36]}
              disableAutoPan
              headerDisabled
            >
              <Box fontFamily="'Instrument Sans', sans-serif" p={1}>
                <Text fontSize="xs" fontWeight="bold" color="#1a1a1a">
                  {geoPhotos[info.index].p.photo.photoType}
                </Text>
                <Text fontSize="10px" color="#888">
                  Clic para ampliar
                </Text>
              </Box>
            </InfoWindow>
          )}
          {info?.kind === 'stop' && stops[info.index] && (
            <InfoWindow
              position={ll(stops[info.index].lat, stops[info.index].lng)}
              pixelOffset={[0, -28]}
              onCloseClick={() => setInfo(null)}
            >
              <Box fontFamily="'Instrument Sans', sans-serif">
                <Text fontSize="sm" fontWeight="700" color="#c8442f">
                  Parada {info.index + 1}
                </Text>
                <Text fontSize="xs" color="#555" mt={1}>
                  {clock(stops[info.index].startAt)} – {clock(stops[info.index].endAt)}
                </Text>
                <Text fontSize="xs" color="#333" mt={0.5}>
                  Duración: {formatDuration(stops[info.index].durationMs)}
                </Text>
              </Box>
            </InfoWindow>
          )}
          {info?.kind === 'event' && stateChanges[info.index] && (
            <EventInfoWindow sc={stateChanges[info.index]} onClose={() => setInfo(null)} dateTime={dateTime} />
          )}
          {info?.kind === 'pause' && pausePins[info.index] && (
            <InfoWindow
              position={ll(pausePins[info.index].lat, pausePins[info.index].lng)}
              pixelOffset={[0, -28]}
              onCloseClick={() => setInfo(null)}
            >
              <Box fontFamily="'Instrument Sans', sans-serif" maxW="260px">
                <Text fontSize="sm" fontWeight="700" color="#8a5f0b">
                  Pausa
                </Text>
                <Text fontSize="xs" color="#333" mt={1}>
                  Motivo: {pausePins[info.index].reason}
                </Text>
                <Text fontSize="xs" color="#555" mt={0.5}>
                  {dateTime(pausePins[info.index].occurredAt)}
                </Text>
                <Text fontSize="xs" color="#888" mt={0.5}>
                  Duración: {pausePins[info.index].durationLabel}
                </Text>
              </Box>
            </InfoWindow>
          )}
        </KMap>

        {children}
      </Box>

      {/* Leyenda */}
      <Flex px={5} py={3} gap={4} rowGap={2} flexWrap="wrap" alignItems="center" borderTop="1px solid" borderColor={colors.border}>
        <LegendItem shape="dot" color="#2a8a87" label="Origen" />
        <LegendItem shape="dot" color="#3d6fa8" label="Destino" />
        {stops.length > 0 && <LegendItem shape="dot" color="#c8442f" label="Parada" />}
        {geoPhotos.length > 0 && <LegendItem shape="square" color="#d49a2a" label="Foto" />}
        {stateChanges.some((sc) => sc.latitude != null && sc.longitude != null) && (
          <LegendItem shape="ring" color={colors.textMuted} label="Evento" />
        )}
        {pausePins.length > 0 && (
          <Flex alignItems="center" gap={1.5}>
            <Flex
              w="14px"
              h="14px"
              borderRadius="full"
              bg="#d49a2a"
              alignItems="center"
              justifyContent="center"
              color="white"
            >
              <Pause size={8} fill="white" />
            </Flex>
            <Text fontSize="11px" color={colors.textSecondary} fontWeight="500">
              Pausa
            </Text>
          </Flex>
        )}
        {colorBySpeed && (
          <>
            <Box w="1px" h="14px" bg={colors.border} mx={1} />
            <Text fontSize="10px" fontWeight="bold" textTransform="uppercase" letterSpacing="0.1em" color={colors.textMuted}>
              Velocidad
            </Text>
            <LegendItem shape="line" color={SPEED_COLORS.stopped} label="Detenido" />
            <LegendItem shape="line" color={SPEED_COLORS.slow} label="Lento" />
            <LegendItem shape="line" color={SPEED_COLORS.medium} label="Medio" />
            <LegendItem shape="line" color={SPEED_COLORS.fast} label="En ruta" />
          </>
        )}
      </Flex>
    </MotionBox>
  )
}

function EventInfoWindow({
  sc,
  onClose,
  dateTime,
}: {
  sc: ShipmentEventResponse
  onClose: () => void
  dateTime: (iso: string) => string
}) {
  const def = getShipmentState(sc.shipmentState)
  return (
    <InfoWindow position={ll(sc.latitude as number, sc.longitude as number)} pixelOffset={[0, -28]} onCloseClick={onClose}>
      <Box fontFamily="'Instrument Sans', sans-serif" maxW="280px">
        <Text fontSize="sm" fontWeight="700" color={def.color}>
          {def.label}
        </Text>
        <Text fontSize="xs" color="#555" mt={1}>
          {dateTime(sc.changedAt)}
        </Text>
        <Text fontSize="xs" color="#888" mt={0.5}>
          Por: {sc.changedByUserName ?? 'Usuario desconocido'}
        </Text>
        {sc.notes && (
          <Text fontSize="xs" color="#333" mt={1} fontStyle="italic">
            "{sc.notes}"
          </Text>
        )}
      </Box>
    </InfoWindow>
  )
}

function LegendItem({ shape, color, label }: { shape: 'dot' | 'square' | 'ring' | 'line'; color: string; label: string }) {
  return (
    <Flex alignItems="center" gap={1.5}>
      {shape === 'square' ? (
        <Box w="10px" h="10px" borderRadius="2px" bg={color} />
      ) : shape === 'line' ? (
        <Box w="14px" h="3px" borderRadius="full" bg={color} />
      ) : shape === 'ring' ? (
        <Box w="10px" h="10px" borderRadius="full" border="2px solid" borderColor={color} />
      ) : (
        <Box w="10px" h="10px" borderRadius="full" bg={color} />
      )}
      <Text fontSize="11px" color={colors.textSecondary} fontWeight="500">
        {label}
      </Text>
    </Flex>
  )
}

function ToggleButton({
  active,
  activeColor,
  icon,
  onClick,
  children,
}: {
  active: boolean
  activeColor: string
  icon: React.ReactNode
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Flex
      as="button"
      alignItems="center"
      gap={2}
      px={3}
      py={1.5}
      borderRadius="lg"
      cursor="pointer"
      fontSize="xs"
      fontWeight="600"
      bg={active ? `${activeColor}20` : colors.bgHover}
      color={active ? activeColor : colors.textMuted}
      border="1px solid"
      borderColor={active ? `${activeColor}40` : 'transparent'}
      _hover={{ bg: active ? `${activeColor}30` : colors.border }}
      transition="all 0.2s"
      onClick={onClick}
    >
      {icon}
      {children}
    </Flex>
  )
}
